'use client';

import { type QueryKey, useQueryClient } from '@unifyapps/network/react-query';
import { useRef } from 'react';

// Synchronously evicts cache entries matching each key in `queryKeys` on the
// first render of the consuming hook. A ref guard ensures the eviction runs
// exactly once per hook instance — re-renders are a no-op.
//
// Use case: collaboration freshness. WS patches update the backend and other
// clients' Zustand stores but never this user's React Query cache. Evicting
// on first render of the data-loader hook means the React Query observer
// reads an empty cache, enters isPending → fires a fresh fetch — giving every
// mount a known-good snapshot regardless of stale cache from before navigation.
//
// Eviction MUST happen before the consuming hook's observer subscribes. The
// in-render ref pattern guarantees that ordering: the eviction is committed
// to the QueryClient before any later hook in the same function body reads
// the cache snapshot via useSyncExternalStore.
//
// Pass FULL query keys (request-specific) — not prefixes — so siblings using
// the same endpoint but with different requests keep their cache intact.
export function useEvictQueriesOnMount(queryKeys: QueryKey[], enabled = true) {
  const queryClient = useQueryClient();
  const hasEvictedRef = useRef(false);
  if (enabled && !hasEvictedRef.current) {
    hasEvictedRef.current = true;
    for (const queryKey of queryKeys) {
      queryClient.removeQueries({ queryKey });
    }
  }
}
